/**
 * §10.3 Mission & Vision — two large glass cards side by side.
 */

import React from 'react'
import { Target, Telescope, Sparkles, ArrowRight } from 'lucide-react'
import {
  Container, Eyebrow, SectionHeading,
} from '@/components/landing/design-system'

export default function MissionVision() {
  return (
    <section className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Mission &amp; Vision</Eyebrow>
          <SectionHeading align="center"
            title={<>Where we are today,{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">where we are going.</span></>}
            subtitle="A concise statement of purpose — the mission we execute against every quarter and the vision that anchors every long-term platform bet."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* MISSION */}
          <div className="relative rounded-3xl border border-brand-gold/30 bg-gradient-to-b from-brand-gold/[0.08] to-white/[0.01] p-6 md:p-8 backdrop-blur-xl overflow-hidden shadow-[0_20px_70px_-30px_rgba(255,193,7,0.35)]">
            <div className="pointer-events-none absolute inset-0 opacity-70"
              style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.14), transparent 70%)' }} />
            <div className="relative flex flex-col gap-4">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-brand-gold/40 bg-brand-gold/[0.08] text-brand-gold w-fit">
                <Target strokeWidth={1.8} style={{ width: 20, height: 20 }} />
              </span>
              <div className="text-[10.5px] font-mono uppercase tracking-wider text-brand-gold">Our Mission</div>
              <div className="text-[19px] md:text-[22px] font-semibold text-white leading-snug">
                To simplify the creation and operation of AI-powered trading businesses through a
                unified, scalable and enterprise-ready SaaS platform.
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {['Unified', 'Scalable', 'Enterprise-ready', 'AI-native'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full border border-brand-gold/25 bg-brand-gold/[0.06] px-2.5 py-1 text-[11px] font-semibold text-brand-gold">
                    <Sparkles className="h-3 w-3" /> {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* VISION */}
          <div className="relative rounded-3xl border border-cyan-400/30 bg-gradient-to-b from-cyan-400/[0.06] to-white/[0.01] p-6 md:p-8 backdrop-blur-xl overflow-hidden shadow-[0_20px_70px_-30px_rgba(34,211,238,0.35)]">
            <div className="pointer-events-none absolute inset-0 opacity-70"
              style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(34,211,238,0.14), transparent 70%)' }} />
            <div className="relative flex flex-col gap-4">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-400/40 bg-cyan-400/[0.08] text-cyan-400 w-fit">
                <Telescope strokeWidth={1.8} style={{ width: 20, height: 20 }} />
              </span>
              <div className="text-[10.5px] font-mono uppercase tracking-wider text-cyan-400">Our Vision</div>
              <div className="text-[19px] md:text-[22px] font-semibold text-white leading-snug">
                To become the leading global infrastructure platform for AI-driven trading
                businesses — enabling innovation through technology, intelligence and automation.
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {['Global infrastructure', 'AI-driven', 'Automation', 'Long-term'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] px-2.5 py-1 text-[11px] font-semibold text-cyan-400">
                    <ArrowRight className="h-3 w-3" /> {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
